import { Fragment, useEffect, useState } from 'react';
import { useAsync } from 'react-async';
import { getCaseloadDisplayFromArray, getCaseloadsByDisciplineId } from '../../api/services/caseloadService';
import Layout from '../../components/layout';
import { Link, useLocation, useHistory } from 'react-router-dom';
import { Notification } from '../../components/notifications';
import LayoutError from '../../components/layoutError';
import moment from 'moment';
import { Export } from '../../components/export';
import DisciplineModal from '../../components/disciplineModal';
import Discipline from '../../api/models/discipline';
import CaseloadDisplay from '../../api/models/caseloadDisplay';
import { getDisciplines } from '../../api/services/meService';
import DataTable from '../../components/dataTable';
import React from 'react';
import { generateCaseloadsCsv, sortAlphabetically } from '../../common/utils';
import Therapist from '../../api/models/therapist';
import CsvData from '../../types/csvData';
import { useAppSelector } from '../../app/hooks';
import CaseloadRejectionMessage from '../../api/models/caseloadRejectionMessage';
import { getRejectedCaseloadMessagesByTherapistUser } from '../../api/services/messageService';

type ViewState = {
  message?: string;
  disciplineId?: number;
  caseloadIds?: number[];
  schoolId?: number;
  districtId?: number;
  caseloads: CaseloadDisplay[];
};

const fetchCaseloads = async ({ disciplineId }: any) => await getCaseloadsByDisciplineId(disciplineId);
const fetchRejectedCaseloadMessages = async (args: any) => await getRejectedCaseloadMessagesByTherapistUser(args[0]);

const ViewCaseload = () => {
  const history = useHistory();
  const { state } = useLocation<ViewState>();
  const user = useAppSelector((state) => state.user);
  const [openDiscipline, setOpenDiscipline] = useState(false);
  const [disciplineId, setDisciplineId] = useState<number>(user.currentDiscipline?.disciplineId ?? -1);
  const [discipline, setDiscipline] = useState<Discipline | undefined>(user.currentDiscipline);
  const [disciplines, setDisciplines] = useState<Discipline[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [data, setData] = useState<any>();
  const [caseloads, setCaseloads] = useState<CaseloadDisplay[]>([]);
  const [rejectedCaseloadMessages, setRejectedCaseloadMessages] = useState<CaseloadRejectionMessage[]>([]);
  const [therapists, setTherapists] = useState<Therapist[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [filter, setFilter] = useState('');
  const [csvData, setCsvData] = useState<CsvData[]>([]);
  const [allCaseloads, setAllCaseloads] = useState<CaseloadDisplay[]>([]);

  useAsync({
    promiseFn: getDisciplines,
    onResolve: (r) => {
      const disciplines: Discipline[] = r.data.disciplines;
      let disciplineId: number = -1;

      if (window.location.search && window.location.search !== '') {
        const search = window.location.search.replace('?', '').split('=');
        if (search && search.length > 0) {
          disciplineId = !search[0] || Number.isNaN(search[0]) ? -1 : Number(search[0]);
        }
      }

      if (state && state.disciplineId && state.disciplineId !== null && state.disciplineId !== undefined) {
        disciplineId = state.disciplineId;
        delete (state.disciplineId);
      } else {
        const alphabetical = sortAlphabetically(disciplines, 'name');
        disciplineId = alphabetical && alphabetical.length > 0 ? alphabetical[0].disciplineId : -1;
      }

      setDisciplines(disciplines);

      setTimeout(() => {
        if (!disciplineId || !discipline) {
          setDisciplineId(disciplineId);
          setDiscipline(disciplines.filter((d) => d.disciplineId === disciplineId)[0]);
        }
      }, 200);
    }
  });

  /**
   * Get the rejected caseload messages
   */
  const asyncRejectedCaseloadMessages = useAsync({
    deferFn: fetchRejectedCaseloadMessages,
    onResolve: (response: any) => {
      setRejectedCaseloadMessages(response.data)
    }
  });
  useEffect(() => {
    if (!asyncRejectedCaseloadMessages.isPending && rejectedCaseloadMessages.length === 0 && user && user.id && user.id > 0) {
      asyncRejectedCaseloadMessages.run(user.id, disciplineId)
    }
  }, [user]);

  const asyncCaseloads = useAsync({
    deferFn: fetchCaseloads,
    onResolve: (response) => {
      setData(response);
      setCaseloads(response.data.caseloads);
      setAllCaseloads(response.data.caseloads);
      setTherapists(response.data.therapists);

      if (state && state !== null && state !== undefined) {
        if (state.caseloads && state.caseloads !== undefined && state.caseloads !== null) {
          const fetchCaseloads = async () => {
            const ids: number[] = [];
            state.caseloads.forEach((caseload) => ids.push(caseload.caseloadId));
            const result = await getCaseloadDisplayFromArray(ids);
            const caseloads: CaseloadDisplay[] = [];
            result.forEach((result) => caseloads.push(result.data.caseload));
            setData(response);
            setCaseloads(caseloads);
          }

          fetchCaseloads();
        }
      }
    }
  });

  useEffect(() => {
    const filteredCaseloads = caseloads && caseloads.length > 0 && caseloads.filter(x => x && x !== undefined && x.disciplineId === disciplineId).length > 0 ? caseloads : allCaseloads.filter(x => x.disciplineId === disciplineId)
    const csvData: CsvData[] = generateCaseloadsCsv(filteredCaseloads, disciplineId, filter);
    setCsvData(csvData);
  }, [caseloads, disciplineId, filter]);

  useEffect(() => {
    setDiscipline(disciplines.find((d) => d.disciplineId === disciplineId));
    asyncCaseloads.run(disciplineId);
  }, [disciplineId]);

  /*useEffect(() => console.log('CsvData: ', csvData), [csvData]);
  useEffect(() => console.log('State: ', state), [state]);*/

  const columns = React.useMemo(
    () => [
      {
        Header: 'Student Name',
        accessor: (s: any) => s.studentLastName + ', ' + s.studentFirstName
      },
      {
        Header: 'Discipline',
        accessor: (s: any) => s.disciplines ?? '',
      },
      {
        Header: 'District',
        accessor: (s: any) => s.district ?? '',
        width: 250,
      },
      {
        Header: 'School',
        accessor: (s: any) => s.school,
      },
      {
        Header: 'DOB',
        accessor: (s: any) => moment(s.dob).format('MM/DD/YYYY'),
      },
      {
        Header: 'Grade',
        accessor: (s: any) => s.grade ?? '',
      },
      {
        Header: 'Status',
        accessor: (s: any) => s.status
      },
      {
        Header: 'IEP/504 Start/End Date',
        accessor: (s: any) => s.iepDate ?? '',
      },
      {
        Header: 'Therapist Type',
        accessor: (s: any) => s.therapyType ?? '',
      },
      {
        Header: 'Edit',
        accessor: (s: any) => (<svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 text-gray-900" viewBox="0 0 20 20" fill="currentColor"><path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"></path></svg>)
      },
      {
        accessor: 'studentId',
        className: 'hidden',
      },
    ],
    //Disabling because we do not need the dependency array
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const csvHeaders = [
    { label: 'Student Name', key: 'studentName' },
    { label: 'Disciplines', key: 'disciplines' },
    { label: 'District', key: 'district' },
    { label: 'School', key: 'school' },
    { label: 'DOB', key: 'dob' },
    { label: 'Grade', key: 'grade' },
    { label: 'Status', key: 'status' },
    { label: 'IEP/504 Start/End Date', key: 'iepDate' },
    { label: 'Therapist Type', key: 'therapyType' },
  ];

  //const csvData = generateCaseloadsCsv(caseloads ?? [], disciplineId, filter);

  const resetCaseloads = () => {
    setCaseloads(allCaseloads);
  }

  if (asyncCaseloads.error) return <LayoutError />;
  return (
    <Layout isLoading={asyncCaseloads.isPending}>
      <Fragment>
        <DisciplineModal
          open={openDiscipline}
          toggle={setOpenDiscipline}
          disciplineId={disciplineId}
          setDiscipline={(d) => { setDisciplineId(d.disciplineId ?? -1); setDiscipline(d); }} />

        {state?.message && <Notification>{state?.message}</Notification>}

        {rejectedCaseloadMessages && rejectedCaseloadMessages.length > 0 &&
          <Notification isSuccess={false} isUserCloseable={true}>
            <h3 className="ml-1 font-bold">Rejected Caseloads</h3>
            <ul className="ml-6 list-disc">
              {rejectedCaseloadMessages.map(x =>
                <li>
                  <Link to={`/messages/${x.threadId}`} className="text-white">{x.message}</Link>
                </li>
              )}
            </ul>
          </Notification>
        }

        {therapists && therapists.length === 0 && (
          <div>
            <label>No disciplines assigned.</label>
          </div>
        )}

        {therapists && therapists.length > 0 && (
          <div>
            <div className='mb-4 text-left'>
              <label>Caseload for</label>
              <div>
                <span className='font-bold' onClick={() => { setOpenDiscipline(true); }}>{disciplines && disciplines?.length > 0 && disciplineId && disciplineId > -1 ? disciplines?.find(x => x.disciplineId == disciplineId)?.name : 'Loading...'}</span>
                <button className='block text-sm link' onClick={() => { setOpenDiscipline(true); }}>
                  Change Discipline
                </button>
              </div>
            </div>

            <div className='mb-4 text-right'>
              {state && state !== null && state !== undefined && (
                <>
                  {caseloads !== allCaseloads && (
                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                    <a className='mr-4 cursor-pointer btn' onClick={() => resetCaseloads()}>Show All</a>
                  )}
                </>
              )}
              <Export
                fileName='Caseload'
                data={csvData}
                headers={csvHeaders}
                className='mr-4'
              ></Export>
              <Link
                className='btn'
                to={{
                  pathname: '/caseload/search',
                  state: { disciplineId: disciplineId, therapists: therapists },
                }}
              >
                Add Student
              </Link>
            </div>
            {/* Caseloads were found */}
            {csvData && csvData !== undefined && csvData !== null && (
              <>
                {csvData.length > 0 && (
                  <div className='overflow-hidden border-b border-gray-200 rounded-lg datatable-editable'>
                    <DataTable
                      columns={columns}
                      data={csvData}
                      showPagination={true}
                      getRowProps={(row: any) => ({
                        onClick: () => history.push(`/caseload/${row.original.caseloadId}`, { disciplineId: disciplineId, idSpecific: 'caseload' })
                      })}
                    />
                  </div>
                )}
                {/* Caseloads were not found */}
                {csvData.length === 0 && (
                  <h4 className='font-medium'>No caseloads were found for {discipline?.name}</h4>
                )}
              </>
            )}
          </div>
        )}
      </Fragment>
    </Layout>
  );
};

export default ViewCaseload;